import { Box, Divider, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useShallow } from "zustand/shallow"

import { CustomerPurchaseOrderLineItemSubtotal } from "./CustomerPurchaseOrderLineItemSubtotal"

import { Button } from "@/components/common/Button"
import type { CustomerPurchaseOrderPricingTotalsProductTotal } from "@/graphql/codegen/graphql"
import { useSalesOrderPermissions } from "@/screens/CustomerOrders/hooks/useSalesOrderPermissions"
import type { CpoLineItemFormValues } from "@/screens/CustomerOrders/schemas"
import { type LineItemOverTotals, formatUomSymbol } from "@/screens/CustomerOrders/utils"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"

interface CustomerPurchaseOrderLineItemFooterProps {
  overTotals: LineItemOverTotals
  productTotal: CustomerPurchaseOrderPricingTotalsProductTotal | undefined
  pallets: CpoLineItemFormValues["pallets"]
  units: CpoLineItemFormValues["units"]
  uom: CpoLineItemFormValues["uom"]
  quantity: CpoLineItemFormValues["quantity"]
  cpoLineItemId: CpoLineItemFormValues["cpoLineItemId"]
  isPricingFetching: boolean
  onDeliveryDateAdd: () => void
}

export const CustomerPurchaseOrderLineItemFooter: FC<CustomerPurchaseOrderLineItemFooterProps> = ({
  overTotals,
  productTotal,
  pallets,
  units: lineItemUnits,
  uom,
  quantity,
  cpoLineItemId,
  isPricingFetching,
  onDeliveryDateAdd,
}) => {
  const { isReadonly } = useSalesOrderPermissions()
  const { totalSoPallets, totalSoUnits, totalUomAmount, isPalletsOverTotal, isUnitsOverTotal, isUomOverTotal } =
    overTotals

  const units = useUnitOfMeasurementsStore(useShallow((state) => state.units))

  const uomId = productTotal?.uom?.uomId ?? uom
  const uomSymbol = formatUomSymbol(uomId ?? null, units)

  return (
    <>
      <Divider />
      <Box className='flex justify-end p-6'>
        <Button
          appearance='outlined'
          startIcon={<AddOutlined />}
          disabled={isReadonly}
          onClick={onDeliveryDateAdd}
          data-testid={`customer-orders.line-item-footer.add-delivery-date-button-${cpoLineItemId}`}
        >
          Delivery Date
        </Button>
      </Box>

      <Box className='flex items-center justify-end gap-6 px-6 pb-6'>
        <Typography variant='subtitle2' className='font-bold text-gray-800'>
          Subtotal
        </Typography>
        <CustomerPurchaseOrderLineItemSubtotal
          label='Pallets'
          total={totalSoPallets}
          target={pallets ?? 0}
          isOverTotal={isPalletsOverTotal}
          isLoading={isPricingFetching}
          data-testid={`customer-orders.line-item-footer.pallets-subtotal-${cpoLineItemId}`}
        />
        <CustomerPurchaseOrderLineItemSubtotal
          label='Units'
          total={totalSoUnits}
          target={lineItemUnits ?? 0}
          isOverTotal={isUnitsOverTotal}
          isLoading={isPricingFetching}
          data-testid={`customer-orders.line-item-footer.units-subtotal-${cpoLineItemId}`}
        />
        <CustomerPurchaseOrderLineItemSubtotal
          label={`Total UoM (${uomSymbol})`}
          total={totalUomAmount}
          target={quantity ?? 0}
          isOverTotal={isUomOverTotal}
          isLoading={isPricingFetching}
          data-testid={`customer-orders.line-item-footer.uom-subtotal-${cpoLineItemId}`}
        />
      </Box>
    </>
  )
}
